import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { ShareButton } from "@/components/ShareButton";
import {
  Clock, Filter, Search, X, Trophy, Calendar, FileText, Newspaper, Award,
  ExternalLink, MapPin, ChevronRight,
} from "lucide-react";
import { showError } from "@/lib/errors";

const TYPE_META = {
  competition: { label: "Compétition", Icon: Trophy, tone: "bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30" },
  event:       { label: "Calendrier",  Icon: Calendar, tone: "bg-amber-100/60 text-amber-800 border-amber-300/60 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30" },
  achievement: { label: "Palmarès",    Icon: Award,   tone: "bg-[hsl(var(--aito-gold))]/20 text-amber-700 border-amber-500/40" },
  article:     { label: "Article",     Icon: Newspaper, tone: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700/30 dark:text-slate-200 dark:border-slate-500/30" },
  memory:      { label: "Mémoire",     Icon: FileText, tone: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-700/30 dark:text-purple-200 dark:border-purple-500/30" },
};

const LEVELS = [
  { v: "local", label: "Local" },
  { v: "territorial", label: "Territorial" },
  { v: "national", label: "National" },
  { v: "regional", label: "Régional" },
  { v: "international", label: "International" },
  { v: "professionnel", label: "Professionnel" },
];

const ALL = "__all__";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function EventRow({ event }) {
  const meta = TYPE_META[event.type] || TYPE_META.article;
  const Icon = meta.Icon;
  const isExternalLink = event.link && /^https?:\/\//.test(event.link);
  const LinkComp = isExternalLink ? "a" : Link;
  const linkProps = isExternalLink ? { href: event.link, target: "_blank", rel: "noreferrer" } : { to: event.link };

  return (
    <Card className="p-4 flex gap-3 items-start aito-card-hover" data-testid={`timeline-event-${event.id}`}>
      <div className={`shrink-0 h-9 w-9 rounded-full border ${meta.tone} flex items-center justify-center`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
          {event.discipline && <Badge variant="secondary" className="text-[10px] capitalize">{event.discipline}</Badge>}
          {event.level && <Badge variant="outline" className="text-[10px]">{event.level}</Badge>}
          {event.medal && <Badge className="text-[10px] bg-[hsl(var(--aito-gold))] text-black">🏅 {event.medal}</Badge>}
          {event.date && <span className="text-[11px] text-muted-foreground ml-auto"><Clock className="h-3 w-3 inline mr-0.5" />{fmtDate(event.date)}</span>}
        </div>
        {event.link ? (
          <LinkComp {...linkProps} className="font-display font-semibold text-sm leading-tight hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 block">
            {event.title}
            {isExternalLink && <ExternalLink className="h-3 w-3 inline ml-1" />}
          </LinkComp>
        ) : (
          <p className="font-display font-semibold text-sm leading-tight">{event.title}</p>
        )}
        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
          {event.athlete_name && (
            <div>
              <span className="font-medium text-foreground">Athlète : </span>
              <Link to={`/athletes/${event.athlete_slug || event.athlete_id}`} className="text-[hsl(var(--aito-lagoon))] hover:underline">
                {event.athlete_name}
              </Link>
            </div>
          )}
          {event.competition_name && event.type !== "competition" && (
            <div><span className="font-medium text-foreground">Compétition : </span>{event.competition_name}</div>
          )}
          {(event.extra?.location || event.extra?.place) && (
            <div className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{event.extra?.location || event.extra?.place}</div>
          )}
          {event.result && <div><span className="font-medium text-foreground">Résultat : </span>{event.result}</div>}
          {event.extra?.media && <div className="italic">{event.extra.media}</div>}
        </div>
      </div>
    </Card>
  );
}

function GroupedTimeline({ events }) {
  // Regroupe par année (events triés desc)
  const groups = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const y = e.date ? new Date(e.date).getUTCFullYear() : "—";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(e);
    }
    return Array.from(map.entries());
  }, [events]);

  return (
    <div className="space-y-6">
      {groups.map(([year, items]) => (
        <section key={year} data-testid={`timeline-year-${year}`}>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display text-2xl font-bold text-[hsl(var(--aito-lagoon))]">{year}</h2>
            <div className="flex-1 h-px bg-border" />
            <Badge variant="outline" className="text-[10px]">{items.length} événement{items.length > 1 ? "s" : ""}</Badge>
          </div>
          <div className="space-y-3">
            {items.map((e) => <EventRow key={e.id} event={e} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function TimelinePage() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disciplines, setDisciplines] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState(ALL);
  const [discipline, setDiscipline] = useState(ALL);
  const [level, setLevel] = useState(ALL);
  const [year, setYear] = useState(ALL);

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (type !== ALL) params.type = type;
    if (discipline !== ALL) params.discipline = discipline;
    if (level !== ALL) params.level = level;
    if (year !== ALL) params.year = parseInt(year);
    if (search.trim()) params.q = search.trim();
    api.get("/timeline", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { setItems([]); showError(e, { prefix: "Timeline :" }); })
      .finally(() => setLoading(false));
  }, [type, discipline, level, year, search]);

  const years = useMemo(() => {
    const set = new Set();
    (items || []).forEach((e) => { if (e.date) set.add(new Date(e.date).getUTCFullYear()); });
    const cur = new Date().getUTCFullYear();
    [cur - 2, cur - 1, cur, cur + 1].forEach((y) => set.add(y));
    return Array.from(set).sort((a, b) => b - a);
  }, [items]);

  const hasFilters = type !== ALL || discipline !== ALL || level !== ALL || year !== ALL || search;
  const clearAll = () => { setType(ALL); setDiscipline(ALL); setLevel(ALL); setYear(ALL); setSearch(""); };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-8" data-testid="timeline-page">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
            <Clock className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
            Timeline sportive
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Toute l'actualité chronologique du sport polynésien : compétitions, résultats, palmarès, articles, événements et entrées mémoire.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">{(items || []).length} événement{(items || []).length > 1 ? "s" : ""}</div>
      </header>

      <div className="mb-4">
        <ShareButton
          kind="timeline"
          slug="page"
          title="Timeline sportive — AITO.PF"
          text="Compétitions, résultats, palmarès et actualités du sport polynésien."
          canonicalPath="/timeline"
          dataTestId="timeline-share-button"
        />
      </div>

      <Card className="p-4 mb-6 space-y-3" data-testid="timeline-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-sm font-medium">Filtres</span>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="timeline-clear-filters" className="ml-auto text-xs h-7">
              <X className="h-3 w-3 mr-1" />Réinitialiser
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="timeline-search"
            placeholder="Rechercher (titre, lieu, athlète, média…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="timeline-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous types</SelectItem>
              {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger data-testid="timeline-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Toutes disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="timeline-filter-level"><SelectValue placeholder="Niveau" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous niveaux</SelectItem>
              {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="timeline-filter-year"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes années</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3" data-testid="timeline-loading">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={hasFilters ? "Aucun événement" : "Aucune timeline disponible."}
          description={hasFilters
            ? "Modifie ou réinitialise tes filtres pour élargir la recherche."
            : "Les premiers événements arriveront dès que des compétitions, résultats ou articles seront validés."
          }
        />
      ) : (
        <GroupedTimeline events={items} />
      )}
    </div>
  );
}
